/**
 * The hotel detail panel — everything `/api/btp/hotels` returns that does not
 * fit in a scannable column.
 *
 * The route already sends 26 fields per property; the table showed 11. So this
 * panel is not new data, it is data that was arriving and being discarded — the
 * other two GDS codes, the street address, the trailing-history breakdown, the
 * contract fields. Same reasoning as the Agreements panel: the columns stay the
 * ones you sort and compare on, and the rest lives one disclosure click away
 * rather than pushing the table into horizontal scroll.
 *
 * The `Hotel` type lives here rather than in page.tsx so the row and the panel
 * cannot disagree about the payload shape, the same way AgreementDetail owns
 * `Agreement`.
 */
import { Field, Group } from '@/features/crm/components/DetailPanel';
import { GdsCodes } from '@/features/crm/components/GdsCodes';
import { contactHref } from '@/features/crm/lib/contact-href';
import { HotelContacts } from '@/features/hotel-supplier/components/HotelContacts';
import { SupplierContactLog } from '@/features/hotel-supplier/components/SupplierContactLog';
import {
  fmtMaybeCalendarDate,
  fmtMaybeInstant,
} from '@/features/crm/lib/dates';
import {
  AgreementRollupSection,
  type AgreementRollup,
} from '@/features/hotel-supplier/components/AgreementRollup';
import { HotelCustomerVolume } from '@/features/hotel-supplier/components/HotelCustomerVolume';

export interface Hotel {
  id: string;
  canonical_id: string;
  name: string;
  city: string | null;
  state: string | null;
  postal: string | null;
  country: string | null;
  address: string | null;
  phone: string | null;
  latitude: number | null;
  longitude: number | null;
  sabre_property_code: string | null;
  travelport_property_code: string | null;
  worldspan_property_code: string | null;
  gds_systems_used: string | null;
  vendor_family: string | null;
  brand: string | null;
  bookings: number | null;
  distinct_customers: number | null;
  nights: number | null;
  spend_usd: number | null;
  first_booking: string | null;
  last_booking: string | null;
  // Contact + negotiated-rate info. Null for properties with no linked contract
  // record — which is most of them today.
  //
  // These three are the RAW FEED values off the one Domo contracts row. They belong
  // to the "Contract record (single row)" group and nowhere else — do NOT render them
  // as "the contact for this property", because a contact we own may supersede them.
  // `display_contact` is the field for that question.
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  /**
   * Who to actually call, with the supersession rule already applied server-side
   * (pickTableContact in customer-management-api/src/lib/hsp/hotel-contact.ts):
   * our own active contact when we have one, otherwise the feed's row labelled as
   * such, otherwise null.
   *
   * Optional so an older or cached payload renders the same as "nobody recorded"
   * rather than throwing — and `display_contact_all_departed` is what separates
   * "everyone we recorded has left" from "we never recorded anyone", which must not
   * look the same on screen.
   */
  display_contact?: {
    name: string;
    email: string | null;
    phone: string | null;
    provenance: 'owned' | 'feed';
  } | null;
  display_contact_owned_total?: number;
  display_contact_all_departed?: boolean;
  negotiated_rate: number | null;
  rate_code: string | null;
  preferred: boolean | null;
  contract_expires_on: string | null;
  /**
   * Agreements on THIS property, across every customer — the answer to "do we
   * already have rates here, and with whom?". Optional so a cached or older
   * payload renders the empty state instead of throwing.
   */
  agreements?: AgreementRollup;
}

/* ── Formatting ───────────────────────────────────────────────────────────── */

const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

// Negotiated rates are nightly figures — keep the cents (unlike aggregate spend).
const usdRate = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Counts, formatted for display. Returns null rather than '—' so `Field` owns
 * the em-dash — and note the null test is explicit: `!v` would blank a real
 * zero, and "this property has 0 bookings" is a fact the team acts on.
 */
export function fmtNum(v: number | null): string | null {
  return v == null ? null : v.toLocaleString('en-US');
}

export function fmtSpend(v: number | null): string | null {
  return v == null ? null : usd.format(v);
}

export function fmtRate(v: number | null): string | null {
  return v == null ? null : usdRate.format(v);
}

/**
 * Average nightly rate actually PAID at this property — `spend / nights`.
 *
 * Asked for by Stacie (2026-08-11) as a main-view column. Both inputs are already
 * mirrored on `hotel_master`, so nothing new is fetched.
 *
 * Nights-weighted by construction, which is the point: dividing total spend by
 * total nights gives what a night here cost us. Averaging per-booking rates
 * instead would let a single one-night stay count as much as a two-week one — the
 * same distinction the consortium observation rollup encodes.
 *
 * Returns null, never 0, when it cannot be computed. A 0 in a rate column reads
 * as a free room; `nights === 0` with real spend is a data artefact, not a
 * bargain, and dividing by it yields Infinity.
 *
 * NOT the same number as `negotiated_rate`, which is a CONTRACT rate off one Domo
 * row that was selected for its contact quality (see the caveat in the Contract
 * group below). This is what was charged; that is what was agreed. They are
 * displayed in separate, separately-labelled columns for exactly that reason.
 */
export function adrFor(h: {
  spend_usd: number | null;
  nights: number | null;
}): number | null {
  if (h.spend_usd == null || h.nights == null || h.nights <= 0) return null;
  const adr = h.spend_usd / h.nights;
  return Number.isFinite(adr) ? adr : null;
}

/** Coordinates as one field — they are only ever read as a pair. */
function fmtCoords(lat: number | null, lon: number | null): string | null {
  if (lat == null || lon == null) return null;
  return `${lat.toFixed(4)}, ${lon.toFixed(4)}`;
}

/* ── The panel ────────────────────────────────────────────────────────────── */

/**
 * Ordered by how the property gets used: where it is and how to reach it, then
 * the GDS codes (the reason this panel exists — the table can only afford to
 * show Sabre), then the booking history that justifies sourcing it, then any
 * contract on file.
 */
export function HotelDetail({ hotel: h }: { hotel: Hotel }) {
  // "Are there contract TERMS on file?", which is narrower than it was on
  // 2026-08-12: the three contact fields used to count toward this, and they moved
  // out to the Contacts section below. Leaving them in would mean a property with a
  // phone number and no rate rendered this group with four dashes and a caveat
  // about rates, rather than saying plainly that no terms are recorded.
  const hasContract =
    h.negotiated_rate != null ||
    h.rate_code != null ||
    h.preferred != null ||
    h.contract_expires_on != null;

  return (
    // `data-canonical-id` carries the key the panel no longer prints. See the
    // note in the Property group.
    <div className="detail-panel" data-canonical-id={h.canonical_id}>
      <Group label="Property" testId="hotel-detail-property">
        <dl className="detail-grid">
          <Field label="Property" value={h.name} />
          {/* Brand removed from the panel AND the main-view column on
              2026-08-11 (Stacie): the chain dimension is not how sourcing is
              worked, and she asked for it gone in both places. Still SEARCHABLE
              on the Hotels page — a hidden column and an unusable search are
              different losses, and only the column was in the way. */}
          <Field label="Chain family" value={h.vendor_family} />
          <Field label="Address" value={h.address} />
          <Field label="City" value={h.city} />
          <Field label="State" value={h.state} />
          <Field label="Postal code" mono value={h.postal} />
          <Field label="Country" value={h.country} />
          <Field label="Phone">
            {/* Encoded via the shared helper, not interpolated: this number comes
                from the Domo mirror and `tel:` takes `;postd=` post-dial digits. */}
            {h.phone ? (
              <a href={contactHref('tel', h.phone)}>{h.phone}</a>
            ) : null}
          </Field>
          <Field
            label="Coordinates"
            mono
            value={fmtCoords(h.latitude, h.longitude)}
          />
        </dl>
      </Group>

      {/* The point of the panel. A rate cannot be loaded without the right code
          for the right GDS, and a property carrying a Sabre code but no
          Worldspan code is a known cause of a load failing — so a blank here is
          a finding, not a formatting problem. */}
      {/* The point of the panel. A rate cannot be loaded without the right code
          for the right GDS, and a property carrying a Sabre code but no
          Worldspan code is a known cause of a load failing — so a blank here is
          a finding, not a formatting problem.

          SHARED with the agreement drawer since 2026-09-11 (item 69): the two
          panels rendered the same four fields from the same record by hand, and
          the agreement one had quietly lost Worldspan.

          CANONICAL ID IS STILL NOT SHOWN. Stacie, 2026-09-03: "Yes, we can hide
          canonical ID for sure." It rides `data-canonical-id` on the panel root
          instead, where support can still read it off the DOM. */}
      <GdsCodes
        codes={{
          sabre: h.sabre_property_code,
          travelport: h.travelport_property_code,
          worldspan: h.worldspan_property_code,
          gdsSystems: h.gds_systems_used,
        }}
        testId="hotel-detail-gds"
      />

      <Group label="Booking history" testId="hotel-detail-history">
        <dl className="detail-grid">
          <Field label="Bookings" value={fmtNum(h.bookings)} />
          <Field label="Nights" value={fmtNum(h.nights)} />
          <Field label="Spend" value={fmtSpend(h.spend_usd)} />
          {/* What a night here actually cost — spend / nights. Sits in Booking
              history, NOT next to the contract rate, because it is an observation
              rather than an agreed figure. */}
          <Field label="Avg nightly (paid)" value={fmtRate(adrFor(h))} />
          {/* How many separate customers booked it — the difference between one
              account's habit and a property worth negotiating company-wide. */}
          <Field
            label="Distinct customers"
            value={fmtNum(h.distinct_customers)}
          />
          <Field
            label="First booking"
            value={fmtMaybeInstant(h.first_booking)}
          />
          <Field label="Last booking" value={fmtMaybeInstant(h.last_booking)} />
        </dl>
      </Group>

      {/* First, because it is the question the team opens a property to answer:
          is anyone already under contract here? A "no" is what turns this row
          into a sourcing candidate.

          ITEM 42 — the empty copy states the RECORD, then the inference, in that
          order. It used to lead with the conclusion ("it is an open sourcing
          candidate"), which on a staging database with no agreements seeded reads
          as the screen being broken: Stacie diagnosed it correctly and still had
          to stop and work out whether she was looking at a bug or a fact. Naming
          the source first ("nothing recorded against this property") is true in
          both environments, and leaves the inference as an inference. */}
      <AgreementRollupSection
        emptyMessage="No agreements are recorded against this property for any customer. If that is the complete picture, it is an open sourcing candidate."
        rollup={h.agreements}
        show="customer"
        testId="hotel-detail-agreements"
      />

      {/* WHO WE DEAL WITH HERE — records we own, so a corrected phone number stays
          corrected. Placed above the contract record because it is the question
          someone opens a property to act on, and because it now owns contacts
          outright: the three contact fields that used to sit in the group below
          moved here on 2026-08-12.

          They had to MOVE rather than be duplicated. The feed's contact appears in
          this list as a labelled, read-only fallback row, so leaving a copy in the
          contract group would have shown the same stale person twice in one panel —
          once looking editable and once not, which is exactly the confusion this
          section exists to end. */}
      <Group label="Contacts" testId="hotel-detail-hotel-contacts">
        <HotelContacts
          hotelCanonicalId={h.canonical_id}
          testId="hotel-contacts"
        />
      </Group>

      {/* NOT "the" contract, and NOT "the" rate.
       *
       * Every field in this group comes from ONE row of the Domo Hotel Contracts
       * feed, chosen per property by `pickBestContact` in the API's
       * lib/hotel-contacts.ts. That ranking optimises for CONTACT quality — has
       * an email, is flagged preferred, latest expiry — and the rate is whatever
       * happened to ride along on the winning row. So on a property where seven
       * customers each hold a different rate, this shows one of the seven, picked
       * for a reason that has nothing to do with rates.
       *
       * Relabelled after Jeff caught it on 2026-08-09. Note the irony the ranking
       * leaves behind now that contacts have moved out: this group keeps the rate
       * that was selected for the quality of a contact it no longer displays. That
       * is not a bug to fix here — it is why the number needs its caveat. */}
      <Group
        label="Contract record (single row)"
        testId="hotel-detail-contract"
      >
        {hasContract ? (
          <dl className="detail-grid">
            <Field
              label="Rate on this record"
              value={fmtRate(h.negotiated_rate)}
            />
            <Field label="Code on this record" mono value={h.rate_code} />
            <Field
              label="Preferred"
              value={h.preferred == null ? null : h.preferred ? 'Yes' : 'No'}
            />
            {/* A calendar date, not an instant — formatted in UTC so it reads as
                the date that was negotiated in every timezone. */}
            <Field
              label="Expires"
              value={fmtMaybeCalendarDate(h.contract_expires_on)}
            />
          </dl>
        ) : null}
        {hasContract ? (
          <p className="detail-note" data-testid="hotel-detail-contract-caveat">
            <span className="muted">
              One row from the contract feed, picked per property by contact
              quality — not the only rate here. This property has{' '}
              {h.distinct_customers ?? 'multiple'} booking customers, who may
              each hold a different rate; see Agreements above.
            </span>
          </p>
        ) : (
          /* The whole group empty means something different from one blank
             field: there is no contract record linked to this property at all.
             Saying so beats seven mysterious dashes that read as a bug. */
          <p className="detail-note">
            <span className="muted">
              No contract record is linked to this property — no contact,
              negotiated rate or expiry has been recorded against it.
            </span>
          </p>
        )}
      </Group>

      {/* Stacie, 2026-08-11 (item 19): "need contacted by/date/method fields that
          update as hotels are contacted". Keyed on canonical_id rather than on an
          agreement, because sourcing outreach happens BEFORE any agreement exists —
          which is the case a per-agreement-only log could not record at all. */}
      <Group label="Contact history" testId="hotel-detail-contacts">
        <SupplierContactLog
          // The property's agreements, for the log form's reason picker — so a
          // hotel-level contact can name which client it was about. Already in
          // this payload (`/api/btp/hotels` returns the rollup), so passing it
          // costs nothing; `shown` is the capped list, which is the right one
          // to offer in a dropdown.
          agreements={h.agreements?.shown}
          hotelCanonicalId={h.canonical_id}
          testId="hotel-contact-log"
        />
      </Group>

      {/* LAST, and the only section that fetches.
       *
       * Stacie, feedback log items 8 + 20: "which customers have volume at this
       * property... top 5 clients", then "most booked clients listed and RACs
       * booked and number of room nights and reservations and average nightly
       * rate and commission yield for each".
       *
       * It answers the question the Booking history group above raises and then
       * refuses to answer — that group shows "Distinct customers: 7" without
       * saying WHICH seven.
       *
       * All three GDS codes are passed, not one: the booking feed carries a
       * single `gds_property_code` that may hold any of them, so sending one
       * would show an empty table for a property booked under another system's
       * code. Name/city/state ride along for properties with no code at all. */}
      <HotelCustomerVolume
        property={{
          gdsCodes: [
            h.sabre_property_code,
            h.travelport_property_code,
            h.worldspan_property_code,
          ],
          name: h.name,
          city: h.city,
          state: h.state,
        }}
      />
    </div>
  );
}
